import { ArrowLeft, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { ProbeLogo } from "@/components/ProbeLogo";
import { formatApiError } from "@/lib/api";

/**
 * Slim full-width shell for "test work" pages (recorder, test detail, run report).
 * Hides the dark sidebar so the user gets the whole viewport for the task.
 * Still renders the impersonation banner + a minimal top bar with breadcrumb.
 */
export function WorkPageShell({ children, backTo = "/projects", backLabel = "All tests" }) {
  const { user, endEmulation } = useAuth();
  const { current: currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const isEmulating = !!user?.emulator;
  const isSuperAdmin = user?.global_role === "super_admin";
  const showCustomerBanner = isSuperAdmin && !isEmulating;

  const handleEndEmulation = async () => {
    try {
      await endEmulation();
      toast.success("Returned to admin");
      navigate("/admin/customers");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC] overflow-hidden">
      {isEmulating && (
        <div
          data-testid="emulation-banner"
          className="bg-[#EFF6FF] border-b border-[#BFDBFE] text-[#1D4ED8] shrink-0"
        >
          <div className="px-4 sm:px-6 h-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 text-sm">
              <Eye size={13} className="shrink-0" />
              <span className="text-[#1D4ED8]/80 shrink-0">Viewing as</span>
              <span className="font-semibold truncate">
                {currentWorkspace?.name || user.email}
              </span>
              <span className="hidden sm:inline text-[#1D4ED8]/60 truncate text-xs">
                · {user.email}
              </span>
            </div>
            <button
              onClick={handleEndEmulation}
              data-testid="end-emulation-button"
              className="inline-flex items-center gap-1.5 bg-[#3B82F6] text-white px-3 h-7 rounded-md text-xs font-semibold hover:bg-[#2563EB] transition-colors shadow-sm"
            >
              <ArrowLeft size={12} />
              Return to Admin
            </button>
          </div>
        </div>
      )}
      {showCustomerBanner && null}
      {/* Top bar */}
      <header className="bg-white border-b border-[#E5E7EB] shrink-0">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between gap-4 max-w-screen-2xl mx-auto w-full">
          <div className="flex items-center gap-5 min-w-0">
            <Link to="/projects" data-testid="work-shell-brand" className="shrink-0">
              <span className="dark:hidden"><ProbeLogo size="md" variant="dark" /></span>
              <span className="hidden dark:inline"><ProbeLogo size="md" variant="light" /></span>
            </Link>
            <span className="hidden sm:inline-block h-5 w-px bg-[#E5E7EB]" />
            <Link
              to={backTo}
              data-testid="work-shell-back-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#0F172A] transition-colors"
            >
              <ArrowLeft size={14} />
              {backLabel}
            </Link>
          </div>
        </div>
      </header>
      <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
    </div>
  );
}
